/* eslint-disable no-case-declarations */
import type { ICreateOrderApplicationCmd } from "@/data/commands/planning/CreateOrderApplicationCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError, IDataSource, IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import { ConsumptionSite, ConsumptionSiteDisplayMode, FulfillmentHandling, FulfillmentPositionDisplayMode, OneFulfillmentPerOrderMode, OrderBillCustomNameSource, OrdererEmailRequirement, OrdererNameRequirement, OrdererPhoneNumberRequirement, OrdererRegisteredRequirement, PaymentMethodMode, SchedulingMode, ServiceType } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IOrderApplicationDetails } from "@/data/model/OrderApplication";
import type { ILinkableEntity, IMultiLanguageText } from "@/data/model/Shared";
import type { IUseCasePaymentMethod } from "@/data/model/UseCasePaymentMethod";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { config, exceptionTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";



import { translate } from "@/shared/localization/language";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import { SelectBootstrap } from "@/operator/elements/SelectBootstrap";



import { i18n } from "@/portal/Trans";
import DurationPicker from "@/portal/components/elements/DurationPicker.vue";
import { VisibleEndUserPropertiesSelect } from "@/portal/elements/VisibleEndUserPropertiesSelect";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import DeliveryAreaTable from "@/portal/forms/DeliveryAreaTable.vue";
import { TastierGlobal } from "@/portal/globalAccess";
import { DomUtils } from "@/portal/helpers/DomUtils";
import { UiNotification } from "@/portal/ui/Notification";


export class OrderApplicationForm extends BaseEntityForm<IOrderApplicationDetails, ICreateOrderApplicationCmd> {
    private originalModel?: IOrderApplicationDetails;
    private nameField: MultiLanguageInput;
    private tableReservationsEnabledSwitch: Switch;
    private $tableReservationsEnabledRow: JQuery;
    private minimumTotalUnitsPerOrderField: NumberInput;
    private maximumTotalUnitsPerOrderField: NumberInput;
    private areItemCommentsAllowedSwitch: Switch;
    private operatorDisplayMainItemPricesOnOfferOverviewSwitch: Switch;
    private operatorDisplayItemImagesSwitch: Switch;
    private useTipSwitch: Switch;
    private useDailyOrderIncrementSwitch: Switch;
    private usePreorderSelect: Select<any>;
    private consumptionSiteSelect: Select<ConsumptionSite>;
    private consumptionSiteDisplayModeSelect: Select<ConsumptionSiteDisplayMode>;
    private fulfillmentPositionDisplayModeSelect: Select<FulfillmentPositionDisplayMode>;
    private oneFulfillmentPerOrderModeSelect: Select<OneFulfillmentPerOrderMode>;
    private fulfillmentHandlingSelect: Select<FulfillmentHandling>;
    private fulfillmentTimeModeSelect: Select<SchedulingMode>;
    private ordererRequirementsEnabledSwitch: Switch;
    private orderPlacedTextField: MultiLanguageInput;
    private beforeOrderPlacedTextField: MultiLanguageInput;
    private beforeOrderPlacedForm: JQuery;
    private fulfillmentHandlingForm: JQuery;
    private enableNotificationsForm: JQuery;
    private enableNotificationsSwitch: Switch;
    private orderPlacedTextIfNotificationsEnabledField: MultiLanguageInput;
    private orderPlacedTextIfNotificationsEnabledForm: JQuery;
    private notificationSection: JQuery;
    private defaultTipPercentField: NumberInput;
    private enablePushNotificationsOnAndroidSwitch: Switch;
    private enablePushNotificationsOnIosSwitch: Switch;
    private defaultTipSection: JQuery;
    private paymentMethodsOptionsSection: JQuery;
    private orderConfirmationSections: JQuery;
    private hideRegisterButtonSwitch: Switch;
    private markUnpaidOrdersOnPrintoutsSwitch: Switch;

    private $enableOrderReminderRow: JQuery;
    private orderReminderEmailSection: JQuery;
    private unpaidOrderEmailSection: JQuery;
    private unpaidOrderEmailCustomSubjectField: MultiLanguageInput;
    private unpaidOrderEmailCustomContentField: MultiLanguageInput;
    private sendEmailConfirmationsForUnpaidOrdersSwitch: Switch;
    private maxPreorderPeriodRow: JQuery;
    private maxPreorderPeriodField: NumberInput;
    private allowCreatingOrderBillsSwitch: Switch;
    private sendOrderReminderBeforeFulfillmentTimePicker: VueComponentInstance<typeof DurationPicker>;
    private sendOrderReminderByPushNotificationSwitch: Switch;
    private sendOrderReminderByEmailSwitch: Switch;
    private orderReminderEmailCustomSubjectField: MultiLanguageInput;
    private orderReminderEmailCustomContentField: MultiLanguageInput;
    private orderPaymentReceiptEmailCustomSubjectField: MultiLanguageInput;
    private orderPaymentReceiptEmailCustomContentField: MultiLanguageInput;
    private $excludedFulfillmentGroupSection: JQuery;
    private excludedFulfillmentGroupsSelect: SelectBootstrap<IFulfillmentGroup>;

    private $deliveryAreasSection: JQuery;
    private $deliveryAreasSectionTitle: JQuery;
    private deliveryAreasTable: VueComponentInstance<typeof DeliveryAreaTable> | undefined;

    private $ordererRequirementsSection: JQuery;
    private firstNameRequirementSelect: Select<OrdererNameRequirement>;
    private lastNameRequirementSelect: Select<OrdererNameRequirement>;
    private emailRequirementSelect: Select<OrdererEmailRequirement>;
    private phoneNumberRequirementSelect: Select<OrdererPhoneNumberRequirement>;
    private registeredRequirementSelect: Select<OrdererRegisteredRequirement>;

    private notificationPickUpOrderMessageTextField: MultiLanguageInput;
    private notificationPickUpOrderTitleTextField: MultiLanguageInput;

    private orderReminderPushNotificationSection: JQuery;
    private orderReminderPushNotificationTitleTextField: MultiLanguageInput;
    private orderReminderPushNotificationMessageTextField: MultiLanguageInput;

    private kioskPaymentPromptField: MultiLanguageInput;

    private useVouchersAndDiscountsSwitch: Switch;
    private promptOperatorsForReceiptAfterPaymentSwitch: Switch;

    private orderBillSection: JQuery;
    private orderBillCustomNameSourceSelect: Select<OrderBillCustomNameSource>;

    private cancellationEmailSection: JQuery;
    private sendCancellationEmailSwitch: Switch;
    private cancellationEmailCustomSubjectField: MultiLanguageInput;
    private cancellationEmailCustomContentField: MultiLanguageInput;

    private reservationConfirmationEmailSection: JQuery;
    private $tableReservationSection: JQuery;
    private sendConfirmationEmailOnReservationAcceptanceSwitch: Switch;
    private reservationConfirmationEmailCustomSubjectField: MultiLanguageInput;
    private reservationConfirmationEmailCustomContentField: MultiLanguageInput;

    private paymentMethodToggles: {
        [index: number]: {
            payNow: Switch;
            payLater: Switch;
            quickPayment: Switch | undefined;
            userSelect: SelectBootstrap<ILinkableEntity>;
        };
    } = {};
    private visibleEndUserPropertiesSelect: VisibleEndUserPropertiesSelect;

    private waiterDataSource: IDataSource<ILinkableEntity> = {
        getData(): Promise<IPaginatedResult<ILinkableEntity>> {
            return Factory.getInstance<LocationDao>(LocationDao).getOperatorsPaginated({
                locationId: TastierGlobal.getLocationSwitch()?.getCurrentLocationId()!,
            });
        },
    };

    constructor(element: JQuery) {
        super(element, Factory.getInstance<PlanningDao>(PlanningDao).useCaseDataSource as any);

        this.beforeOrderPlacedForm = this.$form.find("#before-order-placed-form");
        this.fulfillmentHandlingForm = this.$form.find("#fulfillment-handling-form");
        this.enableNotificationsForm = this.$form.find("#enable-notifications-form");
        this.notificationSection = this.$form.find("#notifications-section");
        this.defaultTipSection = this.$form.find("#default-tip-section");
        this.paymentMethodsOptionsSection = this.$form.find("#pay-later-options-section");
        this.orderConfirmationSections = this.$form.find("#order-confirmation-section");
        this.unpaidOrderEmailSection = this.$form.find("#unpaid-order-email-section");
        this.orderReminderEmailSection = this.$form.find("#order-reminder-email-section");
        this.cancellationEmailSection = this.$form.find("#cancellation-email-section");

        this.reservationConfirmationEmailSection = this.$form.find("#reservation-confirmation-email-section");
        this.$tableReservationSection = this.$form.find("#table-reservation-section");
        this.orderReminderPushNotificationSection = this.$form.find("#order-reminder-push-notification-section");
        this.maxPreorderPeriodRow = this.$form.find(".max-preorder-period-row");
        this.$enableOrderReminderRow = this.$form.find("#enable-order-reminder-email-form");
        this.$tableReservationsEnabledRow = this.$form.find(".table-reservations-enabled-row");
        this.tableReservationsEnabledSwitch = new Switch(
            this.$form.find('.form-control[name="table-reservations-enabled-switch"]')
        );

        this.orderPlacedTextIfNotificationsEnabledForm = this.$form.find(
            "#order-placed-text-if-notifications-enabled-form"
        );

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        const usePreorderSelect = this.$form.find('.form-control[name="usepreorder"]');
        this.usePreorderSelect = Select.createFromSelect(usePreorderSelect);

        this.usePreorderSelect.onChange = (value) => {
            const usePreorder = value === "true";
            if (usePreorder) {
                this.setMaxPreorderPeriodVisibility(true);
            } else {
                this.setMaxPreorderPeriodVisibility(false);
                this.tableReservationsEnabledSwitch.setValue(false);
            }
            this.setBeforeOrderPlacedFormVisibility();
            this.setEnableOrderReminderRowVisibility(usePreorder);
            this.setTableReservationsEnabledRowVisibility(usePreorder);
        };

        const consumptionSiteSelect = this.$form.find('.form-control[name="consumptionsite"]');
        Misc.eachEnum(ConsumptionSite, (i, e) => {
            consumptionSiteSelect.append(
                '<option value="' + i + '">' + i18n("enums.ConsumptionSite." + e) + "</option>"
            );
        });
        this.consumptionSiteSelect = Select.createFromSelect(consumptionSiteSelect);

        const consumptionSiteDisplayModeSelect = this.$form.find('.form-control[name="consumptionsitedisplaymode"]');
        Misc.eachEnum(ConsumptionSiteDisplayMode, (i, e) => {
            consumptionSiteDisplayModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.ConsumptionSiteDisplayMode." + e) + "</option>"
            );
        });
        this.consumptionSiteDisplayModeSelect = Select.createFromSelect(consumptionSiteDisplayModeSelect);
        this.consumptionSiteDisplayModeSelect.onChange = () => {
            this.setDeliveryAreasSectionVisibility();
        };
        const fulfillmentPositionDisplayModeSelect = this.$form.find(
            '.form-control[name="fulfillmentpositiondisplaymode"]'
        );

        Misc.eachEnum(FulfillmentPositionDisplayMode, (i, e) => {
            fulfillmentPositionDisplayModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.FulfillmentPositionDisplayMode." + e) + "</option>"
            );
        });

        this.fulfillmentPositionDisplayModeSelect = Select.createFromSelect(fulfillmentPositionDisplayModeSelect);
        this.fulfillmentPositionDisplayModeSelect.onChange = (value) => {
            const selectedFulfillmentPositionDisplayMode = Number(value);

            if (selectedFulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.None) {
                this.fulfillmentHandlingSelect.setValue(FulfillmentHandling.None);
                this.enableNotificationsSwitch.setValue(false);
                this.setEnableNotificationFormVisibility(FulfillmentHandling.None);
            } else if (this.fulfillmentHandlingSelect.getValue() == FulfillmentHandling.None.toString()) {
                this.fulfillmentHandlingSelect.setValue(FulfillmentHandling.OperatorAndApp);
                this.setEnableNotificationFormVisibility(FulfillmentHandling.OperatorAndApp);
            }

            const orderPlacedTextIfNotificationsEnabled = this.getDefaultOrderPlacedTextIfNotificationsEnabled(
                selectedFulfillmentPositionDisplayMode
            );
            this.orderPlacedTextIfNotificationsEnabledField.setValues(orderPlacedTextIfNotificationsEnabled);

            this.setOrderPlacedTextIfNotificationsEnabledFormVisibility();
            this.setFulfillmentHandlingSectionVisibility(selectedFulfillmentPositionDisplayMode);
            this.setExcludedFulfillmentGroupsSectionVisibility(selectedFulfillmentPositionDisplayMode);
        };

        this.$excludedFulfillmentGroupSection = this.$form.find("#excluded-fulfillment-groups-section");

        this.excludedFulfillmentGroupsSelect = new SelectBootstrap<IFulfillmentGroup>(
            this.$form.find('.form-control[name="excludeFulfillmentGroupSelect"]'),
            Factory.getInstance<CatalogDao>(CatalogDao).fulfillmentGroupDataSource,
            (c) => (c.internalName === undefined || c.internalName === null ? translate(c.name) : c.internalName),
            false,
            i18n("general.none"),
            undefined,
            true
        );

        const fulfillmentHandlingSelect = this.$form.find('.form-control[name="fulfillmenthandling"]');
        Misc.eachEnum(FulfillmentHandling, (i, e) => {
            if (i === FulfillmentHandling.None) {
                fulfillmentHandlingSelect.append(
                    '<option class="hidden" value="' + i + '">' + i18n("enums.FulfillmentHandling." + e) + "</option>"
                );
            } else {
                fulfillmentHandlingSelect.append(
                    '<option value="' + i + '">' + i18n("enums.FulfillmentHandling." + e) + "</option>"
                );
            }
        });

        const oneFulfillmentPerOrderModeSelect = this.$form.find('.form-control[name="oneFulfillmentPerOrderMode"]');
        Misc.eachEnum(OneFulfillmentPerOrderMode, (i, e) => {
            oneFulfillmentPerOrderModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.OneFulfillmentPerOrderMode." + e) + "</option>"
            );
        });
        this.oneFulfillmentPerOrderModeSelect = Select.createFromSelect(oneFulfillmentPerOrderModeSelect);

        this.fulfillmentHandlingSelect = Select.createFromSelect(fulfillmentHandlingSelect);
        this.fulfillmentHandlingSelect.onChange = (value) => {
            const selectedFulfillmentHandling = Number(value);
            if (selectedFulfillmentHandling !== FulfillmentHandling.OperatorAndApp) {
                this.enableNotificationsSwitch.setValue(false);
            }

            this.setOrderPlacedTextIfNotificationsEnabledFormVisibility();
            this.setEnableNotificationFormVisibility(selectedFulfillmentHandling);
        };

        const schedulingModeSelect = this.$form.find('.form-control[name="fulfillmenttimemode"]');
        Misc.eachEnum(SchedulingMode, (i, e) => {
            schedulingModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.FulfillmentTimeMode." + e) + "</option>"
            );
        });
        this.fulfillmentTimeModeSelect = Select.createFromSelect(schedulingModeSelect);
        this.fulfillmentTimeModeSelect.onChange = () => {
            this.setBeforeOrderPlacedFormVisibility();
            this.setDeliveryAreasSectionVisibility();
        };

        this.ordererRequirementsEnabledSwitch = new Switch(
            this.$form.find('.form-control[name="orderer-requirements-enabled"]')
        );
        this.ordererRequirementsEnabledSwitch.onChange = (isEnabled: boolean) => {
            this.setOrdererRequirementsSectionVisibility(isEnabled);
        };

        const firstNameRequirementSelect = this.$form.find('.form-control[name="first-name-requirement"]');
        Misc.eachEnum(OrdererNameRequirement, (i, e) => {
            firstNameRequirementSelect.append(
                '<option value="' + i + '">' + i18n("enums.OrdererRequirement." + e) + "</option>"
            );
        });

        this.firstNameRequirementSelect = Select.createFromSelect(firstNameRequirementSelect);

        const lastNameRequirementSelect = this.$form.find('.form-control[name="last-name-requirement"]');
        Misc.eachEnum(OrdererNameRequirement, (i, e) => {
            lastNameRequirementSelect.append(
                '<option value="' + i + '">' + i18n("enums.OrdererRequirement." + e) + "</option>"
            );
        });

        this.lastNameRequirementSelect = Select.createFromSelect(lastNameRequirementSelect);

        const emailRequirementSelect = this.$form.find('.form-control[name="email-requirement"]');
        Misc.eachEnum(OrdererEmailRequirement, (i, e) => {
            if (i !== OrdererEmailRequirement.ConfirmedCancelOrder)
                emailRequirementSelect.append(
                    '<option value="' + i + '">' + i18n("enums.OrdererRequirement." + e) + "</option>"
                );
        });

        this.emailRequirementSelect = Select.createFromSelect(emailRequirementSelect);

        const phoneNumberRequirementSelect = this.$form.find('.form-control[name="phone-number-requirement"]');
        Misc.eachEnum(OrdererPhoneNumberRequirement, (i, e) => {
            if (i !== OrdererPhoneNumberRequirement.ConfirmedBlockOrder)
                phoneNumberRequirementSelect.append(
                    '<option value="' + i + '">' + i18n("enums.OrdererRequirement." + e) + "</option>"
                );
        });

        this.phoneNumberRequirementSelect = Select.createFromSelect(phoneNumberRequirementSelect);

        const registeredRequirementSelect = this.$form.find('.form-control[name="registered-requirement"]');
        Misc.eachEnum(OrdererRegisteredRequirement, (i, e) => {
            registeredRequirementSelect.append(
                '<option value="' + i + '">' + i18n("enums.OrdererRequirement." + e) + "</option>"
            );
        });

        this.registeredRequirementSelect = Select.createFromSelect(registeredRequirementSelect);

        this.$ordererRequirementsSection = this.$form.find("#orderer-requirements-section");
        this.allowCreatingOrderBillsSwitch = new Switch(this.$form.find('input[name="allowCreatingOrderBills"]'));
        this.allowCreatingOrderBillsSwitch.onChange = (isEnabled: boolean) => {
            this.setOrderBillSectionVisibility(isEnabled);
        };

        this.minimumTotalUnitsPerOrderField = new NumberInput(
            this.$form.find('.form-control[name="minimumTotalUnitsPerOrder"]'),
            {
                min: 1,
                step: 1,
                decimals: 0,
                required: false,
            }
        );
        this.maximumTotalUnitsPerOrderField = new NumberInput(
            this.$form.find('.form-control[name="maximumTotalUnitsPerOrder"]'),
            {
                min: 1,
                step: 1,
                decimals: 0,
                required: false,
            }
        );
        this.areItemCommentsAllowedSwitch = new Switch(this.$form.find('input[name="areItemCommentsAllowed"]'));
        this.operatorDisplayMainItemPricesOnOfferOverviewSwitch = new Switch(
            this.$form.find('input[name="operatorDisplayMainItemPricesOnOfferOverview"]')
        );
        this.operatorDisplayItemImagesSwitch = new Switch(
            this.$form.find('input[name="operatorDisplayItemImages"]')
        );
        this.useTipSwitch = new Switch(this.$form.find('input[name="usetip"]'));
        this.useTipSwitch.onChange = (value) => {
            this.setDefaultTipSectionVisibility(value);
        };

        this.hideRegisterButtonSwitch = new Switch(this.$form.find('input[name="hideregisterbutton"]'));
        this.markUnpaidOrdersOnPrintoutsSwitch = new Switch(
            this.$form.find('input[name="markunpaidordersonprintouts"]')
        );

        this.sendEmailConfirmationsForUnpaidOrdersSwitch = new Switch(
            this.$form.find('input[name="sendEmailConfirmationsForUnpaidOrders"]')
        );

        this.sendEmailConfirmationsForUnpaidOrdersSwitch.onChange = (isEnabled) => {
            this.setUnpaidOrderEmailSectionVisibility(isEnabled);
        };

        this.useDailyOrderIncrementSwitch = new Switch(this.$form.find('input[name="usedailyorderincrement"]'));
        this.orderPlacedTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderplacedtext"]')
        );
        this.beforeOrderPlacedTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="beforeorderplacedtext"]')
        );
        this.enableNotificationsSwitch = new Switch(this.$form.find('input[name="enablepushnotifications"]'));
        this.enableNotificationsSwitch.onChange = (value) => {
            this.setNotificationSectionVisibility(value);

            if (value) {
                this.enablePushNotificationsOnAndroidSwitch.setValue(true);
                this.enablePushNotificationsOnIosSwitch.setValue(true);
            }

            this.setPickUpPushNotificationTitle();
            this.setPickUpPushNotificationMessage();
            this.setOrderPlacedTextIfNotificationsEnabledFormVisibility();
        };

        this.sendOrderReminderByEmailSwitch = new Switch(this.$form.find('input[name="sendOrderReminderByEmail"]'));
        this.sendOrderReminderByEmailSwitch.onChange = (isEnabled) => {
            this.setOrderReminderEmailSectionVisibility(isEnabled);
        };

        this.orderReminderEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderReminderEmailCustomSubjectField"]')
        );

        this.orderReminderEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderReminderEmailCustomContentField"]')
        );

        this.sendCancellationEmailSwitch = new Switch(this.$form.find('input[name="sendCancellationEmail"]'));
        this.sendCancellationEmailSwitch.onChange = (isEnabled) => {
            this.setCancellationEmailSectionVisibility(isEnabled);
        };

        this.cancellationEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="cancellationEmailCustomSubjectField"]')
        );

        this.cancellationEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="cancellationEmailCustomContentField"]')
        );

        // Reservation confirmation
        this.sendConfirmationEmailOnReservationAcceptanceSwitch = new Switch(
            this.$form.find('input[name="sendConfirmationEmailOnReservationAcceptanceSwitch"]')
        );
        this.sendConfirmationEmailOnReservationAcceptanceSwitch.onChange = (isEnabled) => {
            this.setReservationConfirmationEmailSectionVisibility(isEnabled);
        };

        this.reservationConfirmationEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="reservationConfirmationEmailCustomSubjectField"]')
        );

        this.reservationConfirmationEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="reservationConfirmationEmailCustomContentField"]')
        );

        this.orderPaymentReceiptEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderPaymentReceiptEmailCustomSubjectField"]')
        );

        this.orderPaymentReceiptEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderPaymentReceiptEmailCustomContentField"]')
        );

        this.sendOrderReminderBeforeFulfillmentTimePicker = createVueComponent(
            DurationPicker,
            {
                maxHours: 168,
            },
            this.$form.find(".order-reminder-email-time-picker-container")[0]
        ).mountedComponent;
        this.sendOrderReminderByPushNotificationSwitch = new Switch(
            this.$form.find('input[name="sendOrderReminderByPushNotification"]')
        );
        this.sendOrderReminderByPushNotificationSwitch.onChange = (value) => {
            this.setOrderReminderPushNotificationSectionVisibility(value);
        };
        this.enablePushNotificationsOnAndroidSwitch = new Switch(
            this.$form.find('input[name="enablepushnotificationsonandroid"]')
        );
        this.enablePushNotificationsOnIosSwitch = new Switch(
            this.$form.find('input[name="enablepushnotificationsonios"]')
        );

        this.notificationPickUpOrderMessageTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="messagepickupordertext"]')
        );
        this.notificationPickUpOrderTitleTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="titlepickupordertext"]')
        );

        this.orderReminderPushNotificationTitleTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderReminderPushNotificationTitle"]')
        );

        this.orderReminderPushNotificationMessageTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderReminderPushNotificationMessage"]')
        );

        this.kioskPaymentPromptField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="kioskPaymentPrompt"]')
        );

        this.orderPlacedTextIfNotificationsEnabledField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="orderplacedtextifnotificationsenabled"]')
        );

        this.unpaidOrderEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="unpaidOrderEmailCustomSubjectField"]')
        );

        this.unpaidOrderEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="unpaidOrderEmailCustomContentField"]')
        );

        this.$form.find("#addordernumber").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.notificationPickUpOrderMessageTextField.appendText("[ordernumber]");
            return false;
        });

        this.$form.find("#order-payment-receipt-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.orderPaymentReceiptEmailCustomSubjectField.appendText("[LocationName]");
        });

        this.$form.find("#reservation-confirmation-email-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.reservationConfirmationEmailCustomSubjectField.appendText("[LocationName]");
        });

        this.$form.find("#cancellation-email-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.cancellationEmailCustomSubjectField.appendText("[LocationName]");
        });

        this.$form.find("#order-reminder-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.orderReminderEmailCustomSubjectField.appendText("[LocationName]");
        });

        this.$form.find("#unpaid-orders-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.unpaidOrderEmailCustomSubjectField.appendText("[LocationName]");
        });

        this.$form.find("#addtablename").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.orderPlacedTextField.appendText("[tablename]");

            return false;
        });

        this.maxPreorderPeriodField = new NumberInput(this.$form.find('.form-control[name="max-preorder-period"]'), {
            min: 1,
            max: 93,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.defaultTipPercentField = new NumberInput(this.$form.find('.form-control[name="defaulttip"]'), {
            min: 0,
            max: 10,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.visibleEndUserPropertiesSelect = new VisibleEndUserPropertiesSelect(
            this.$form.find('.form-control[name="visibleEndUserProperties"]')
        );

        this.useVouchersAndDiscountsSwitch = new Switch(this.$form.find('input[name="useVouchersAndDiscountsSwitch"]'));
        this.promptOperatorsForReceiptAfterPaymentSwitch = new Switch(
            this.$form.find('input[name="promptOperatorsForReceiptAfterPayment"]')
        );

        this.$deliveryAreasSection = this.$form.find("#delivery-areas-section");
        this.$deliveryAreasSectionTitle = this.$form.find("#delivery-areas-section-title");

        if (this.$form.find("#delivery-area-table").length > 0) {
            this.deliveryAreasTable = createVueComponent(
                DeliveryAreaTable,
                {},
                this.$form.find("#delivery-area-table")[0],
                true
            ).mountedComponent;
        }

        this.orderBillSection = this.$form.find(".order-bill-section");
        const createOrderBillWithNameSelect = this.$form.find('.form-control[name="createOrderBillWithName"]');
        Misc.eachEnum(OrderBillCustomNameSource, (i, e) => {
            createOrderBillWithNameSelect.append(
                '<option value="' + i + '">' + i18n("enums.CreateOrderBillWithName." + e) + "</option>"
            );
        });

        this.orderBillCustomNameSourceSelect = Select.createFromSelect(createOrderBillWithNameSelect);
    }

    private setFulfillmentHandlingSectionVisibility(
        selectedFulfillmentPositionDisplayMode: FulfillmentPositionDisplayMode
    ) {
        if (selectedFulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.None) {
            this.fulfillmentHandlingForm.hide();
        } else {
            this.fulfillmentHandlingForm.show();
        }
    }

    private setExcludedFulfillmentGroupsSectionVisibility(
        selectedFulfillmentPositionDisplayMode: FulfillmentPositionDisplayMode
    ) {
        if (selectedFulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.None) {
            this.$excludedFulfillmentGroupSection.hide();
        } else {
            this.$excludedFulfillmentGroupSection.show();
        }
    }

    private setOrderBillSectionVisibility(show: boolean) {
        if (show) {
            this.orderBillSection.show();
        } else {
            this.orderBillSection.hide();
        }
    }

    private setDefaultTipSectionVisibility(useTip: boolean) {
        if (useTip) {
            this.defaultTipSection.show();
        } else {
            this.defaultTipSection.hide();
        }
    }

    private setOrdererRequirementsSectionVisibility(show: boolean) {
        if (show) {
            this.$ordererRequirementsSection.show();
        } else {
            this.$ordererRequirementsSection.hide();
        }
    }

    private setEnableNotificationFormVisibility(selectedFulfillmentHandling: FulfillmentHandling) {
        if (selectedFulfillmentHandling === FulfillmentHandling.OperatorAndApp) {
            this.enableNotificationsForm.show();
        } else {
            this.enableNotificationsForm.hide();
        }
    }

    private setDeliveryAreasSectionVisibility() {
        const fulfillmentTimeMode = parseInt(this.fulfillmentTimeModeSelect.getValue()!);
        const consumptionSiteDisplayMode = parseInt(this.consumptionSiteDisplayModeSelect.getValue()!);

        if (
            fulfillmentTimeMode === SchedulingMode.DeliveryTime &&
            consumptionSiteDisplayMode === ConsumptionSiteDisplayMode.DeliveryAddress
        ) {
            this.$deliveryAreasSection.show();
        } else {
            this.$deliveryAreasSection.hide();
        }
    }

    private setNotificationSectionVisibility(enableNotifications: boolean) {
        if (enableNotifications) {
            this.notificationSection.show();
        } else {
            this.notificationSection.hide();
        }
    }

    private setOrderReminderPushNotificationSectionVisibility(enableOrderReminderPushNotification: boolean) {
        if (enableOrderReminderPushNotification) {
            this.orderReminderPushNotificationSection.show();
        } else {
            this.orderReminderPushNotificationSection.hide();
            this.setDefaultOrderReminderPushNotificationTitle();
            this.setDefaultOrderReminderPushNotificationMessage();
        }
    }

    private setMaxPreorderPeriodVisibility(showRow: boolean) {
        if (showRow) {
            this.maxPreorderPeriodRow.show();
        } else {
            this.maxPreorderPeriodRow.hide();
        }
    }

    private setTableReservationsEnabledRowVisibility(show: boolean) {
        if (show) {
            this.$tableReservationsEnabledRow.show();
        } else {
            this.$tableReservationsEnabledRow.hide();
            this.tableReservationsEnabledSwitch.setValue(false);
        }
    }

    private setBeforeOrderPlacedFormVisibility() {
        const usePreorder = this.usePreorderSelect.getValue() === "true";
        if (!usePreorder) {
            this.beforeOrderPlacedForm.hide();
            this.setDefaultMessageToUserBeforeOrder();
        } else {
            this.beforeOrderPlacedForm.show();
        }
    }

    private setEnableOrderReminderRowVisibility(usePreorder: boolean) {
        if (usePreorder) {
            this.$enableOrderReminderRow.show();
        } else {
            this.$enableOrderReminderRow.hide();
            this.sendOrderReminderByEmailSwitch.setValue(false);
        }
    }

    private setTableReservationSectionVisibility(tableReservationEnabled: boolean) {
        if (tableReservationEnabled) {
            this.$tableReservationSection.show();
            this.sendConfirmationEmailOnReservationAcceptanceSwitch.setValue(true);
        } else {
            this.$tableReservationSection.hide();
            this.sendConfirmationEmailOnReservationAcceptanceSwitch.setValue(false);
        }
    }

    private setDefaultMessageToUserBeforeOrder() {
        const defaultMessage = this.getDefaultMessageToUserBeforeOrder();
        this.beforeOrderPlacedTextField.setValues(defaultMessage);
    }

    private setDefaultOrderReminderPushNotificationMessage() {
        const defaultMessage = this.getDefaultOrderReminderPushNotificationMessage();
        this.orderReminderPushNotificationMessageTextField.setValues(defaultMessage);
    }

    private setDefaultOrderReminderPushNotificationTitle() {
        const defaultMessage = this.getDefaultOrderReminderPushNotificationTitle();
        this.orderReminderPushNotificationTitleTextField.setValues(defaultMessage);
    }

    private setPickUpPushNotificationTitle() {
        const defaultMessage = this.getDefaultPickUpOrderPushNotificationTitle();
        this.notificationPickUpOrderTitleTextField.setValues(defaultMessage);
    }

    private setPickUpPushNotificationMessage() {
        const defaultMessage = this.getDefaultPickUpOrderPushNotificationMessage();
        this.notificationPickUpOrderMessageTextField.setValues(defaultMessage);
    }

    private setOrderPlacedTextIfNotificationsEnabledFormVisibility() {
        const pushNotificationEnabled = this.enableNotificationsSwitch.getValue();
        const fulfillmentPositionDisplayMode = Number(this.fulfillmentPositionDisplayModeSelect.getValue());
        const fulfillmentHandling = Number(this.fulfillmentHandlingSelect.getValue());

        if (
            pushNotificationEnabled &&
            fulfillmentPositionDisplayMode !== FulfillmentPositionDisplayMode.None &&
            fulfillmentHandling === FulfillmentHandling.OperatorAndApp
        ) {
            this.orderPlacedTextIfNotificationsEnabledForm.show();
        } else {
            this.orderPlacedTextIfNotificationsEnabledForm.hide();
        }
    }

    public setFormData(model?: IOrderApplicationDetails) {
        const useTip = model === undefined ? false : model.useTip;
        const defaultTipPercent = model === undefined || model.defaultTipPercent == null ? 5 : model.defaultTipPercent;
        const fulfillmentPositionDisplayMode =
            model === undefined ? FulfillmentPositionDisplayMode.None : model.fulfillmentPositionDisplayMode;
        const fulfillmentHandling = model === undefined ? FulfillmentHandling.None : model.fulfillmentHandling;
        const enableNotifications = model === undefined ? false : model.enableNotifications;
        const enablePushNotificationsOnAndroid = model === undefined ? false : model.enablePushNotificationsOnAndroid;
        const enablePushNotificationsOnIos = model === undefined ? false : model.enablePushNotificationsOnIos;
        const markUnpaidOrdersOnPrintouts = model === undefined ? true : model.markUnpaidOrdersOnPrintouts;
        const sendEmailConfirmationsForUnpaidOrders =
            model === undefined ? false : model.sendEmailConfirmationsForUnpaidOrders;
        const allowCreatingOrderBills = model === undefined ? false : model.allowCreatingOrderBills;
        const sendOrderReminderByEmail = model === undefined ? false : model.sendOrderReminderByEmail;
        const sendCancellationEmail = model === undefined ? false : model.sendCancellationEmail;
        const sendConfirmationEmailOnReservationAcceptance =
            model === undefined ? false : model.sendConfirmationEmailOnReservationAcceptance;
        const sendOrderReminderByPushNotification =
            model === undefined ? false : model.sendOrderReminderByPushNotification;

        const schedulingMode = model === undefined ? SchedulingMode.None : model.schedulingMode;
        const oneFulfillmentPerOrderMode =
            model === undefined ? OneFulfillmentPerOrderMode.NotForced : model.oneFulfillmentPerOrderMode;
        const usePreorder = model === undefined ? "false" : model.usePreorder ? "true" : "false";
        const enableSignUpBeforePay = model === undefined ? true : model.enableSignUpBeforePay;
        this.originalModel = model;
        this.updateName(model === undefined ? "" : translate(model.name));
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.minimumTotalUnitsPerOrderField.setValue(model === undefined ? undefined : model.minimumTotalUnitsPerOrder);
        this.maximumTotalUnitsPerOrderField.setValue(model === undefined ? undefined : model.maximumTotalUnitsPerOrder);
        this.areItemCommentsAllowedSwitch.setValue(model === undefined ? true : model.areItemCommentsAllowed);
        this.operatorDisplayMainItemPricesOnOfferOverviewSwitch.setValue(
            model === undefined ? false : model.operatorDisplayMainItemPricesOnOfferOverview
        );
        this.operatorDisplayItemImagesSwitch.setValue(
            model === undefined ? false : model.operatorDisplayItemImages
        );
        this.useTipSwitch.setValue(useTip);
        this.useDailyOrderIncrementSwitch.setValue(model === undefined ? false : model.useDailyOrderIncrement);
        this.usePreorderSelect.setValue(usePreorder);
        this.consumptionSiteSelect.setValue(model === undefined ? ConsumptionSite.OnSite : model.consumptionSite);
        this.consumptionSiteDisplayModeSelect.setValue(
            model === undefined ? ConsumptionSiteDisplayMode.None : model.consumptionSiteDisplayMode
        );

        this.notificationPickUpOrderMessageTextField.setValues(
            model === undefined ? undefined : model.pushNotificationPickUpOrderMessage
        );
        this.notificationPickUpOrderTitleTextField.setValues(
            model === undefined ? undefined : model.pushNotificationPickUpOrderTitle
        );
        this.fulfillmentPositionDisplayModeSelect.setValue(fulfillmentPositionDisplayMode);
        this.fulfillmentHandlingSelect.setValue(fulfillmentHandling);
        this.fulfillmentTimeModeSelect.setValue(schedulingMode);
        this.ordererRequirementsEnabledSwitch.setValue(
            (model?.ordererRequirements.enabledFieldRequirementsCount ?? 0) > 0
        );
        this.orderPlacedTextField.setValues(model === undefined ? undefined : model.orderPlacedText);
        this.beforeOrderPlacedTextField.setValues(model === undefined ? undefined : model.beforeOrderPlacedText);
        this.enableNotificationsSwitch.setValue(enableNotifications);
        this.enablePushNotificationsOnAndroidSwitch.setValue(enablePushNotificationsOnAndroid);
        this.enablePushNotificationsOnIosSwitch.setValue(enablePushNotificationsOnIos);
        this.defaultTipPercentField.setValue(defaultTipPercent);
        this.hideRegisterButtonSwitch.setValue(!enableSignUpBeforePay);
        this.markUnpaidOrdersOnPrintoutsSwitch.setValue(markUnpaidOrdersOnPrintouts);
        this.sendEmailConfirmationsForUnpaidOrdersSwitch.setValue(sendEmailConfirmationsForUnpaidOrders);
        this.allowCreatingOrderBillsSwitch.setValue(allowCreatingOrderBills);
        this.maxPreorderPeriodField.setValue(model === undefined ? undefined : model.maxPreorderPeriod);
        this.orderPlacedTextIfNotificationsEnabledField.setValues(
            model == null ? undefined : model.orderPlacedTextIfNotificationsEnabled
        );

        this.visibleEndUserPropertiesSelect.setValues(model?.visibleEndUserProperties);
        this.useVouchersAndDiscountsSwitch.setValue(model?.useVouchersAndDiscounts);
        this.promptOperatorsForReceiptAfterPaymentSwitch.setValue(model?.promptOperatorsForReceiptAfterPayment);
        this.setOrderBillSectionVisibility(model?.allowCreatingOrderBills ?? false);

        this.oneFulfillmentPerOrderModeSelect.setValue(oneFulfillmentPerOrderMode);
        this.sendOrderReminderByEmailSwitch.setValue(sendOrderReminderByEmail);
        this.sendCancellationEmailSwitch.setValue(sendCancellationEmail);
        this.sendOrderReminderByPushNotificationSwitch.setValue(sendOrderReminderByPushNotification);
        this.orderReminderPushNotificationTitleTextField.setValues(
            model == null ? undefined : model.orderReminderPushNotificationTitle
        );
        this.orderReminderPushNotificationMessageTextField.setValues(
            model == null ? undefined : model.orderReminderPushNotificationMessage
        );
        this.kioskPaymentPromptField.setValues(model == null ? undefined : model.kioskPaymentPrompt);
        this.firstNameRequirementSelect.setValue(
            model?.ordererRequirements.firstNameRequirement ?? OrdererNameRequirement.NotRequired
        );
        this.lastNameRequirementSelect.setValue(
            model?.ordererRequirements.lastNameRequirement ?? OrdererNameRequirement.NotRequired
        );
        this.emailRequirementSelect.setValue(
            model?.ordererRequirements.emailRequirement ?? OrdererEmailRequirement.NotRequired
        );
        this.phoneNumberRequirementSelect.setValue(
            model?.ordererRequirements.phoneNumberRequirement ?? OrdererPhoneNumberRequirement.NotRequired
        );
        this.registeredRequirementSelect.setValue(
            model?.ordererRequirements.registeredRequirement ?? OrdererRegisteredRequirement.NotRequired
        );

        this.excludedFulfillmentGroupsSelect.setValues(model?.excludedFulfillmentGroupIds ?? []);

        this.orderBillCustomNameSourceSelect.setValue(
            model?.orderBillCustomNameSource ?? OrderBillCustomNameSource.OrderNumber
        );

        this.sendOrderReminderBeforeFulfillmentTimePicker.setDuration(
            model !== undefined ? model.sendOrderReminderBeforeFulfillmentTime : undefined
        );

        this.unpaidOrderEmailCustomSubjectField.setValues(
            model == null ? undefined : model.unpaidOrderEmailCustomSubject
        );

        this.unpaidOrderEmailCustomContentField.setValues(
            model == null ? undefined : model.unpaidOrderEmailCustomContent
        );

        this.orderPaymentReceiptEmailCustomSubjectField.setValues(
            model == null ? undefined : model.orderPaymentReceiptEmailCustomSubject
        );

        this.orderPaymentReceiptEmailCustomContentField.setValues(
            model == null ? undefined : model.orderPaymentReceiptEmailCustomContent
        );

        this.orderReminderEmailCustomSubjectField.setValues(
            model == null ? undefined : model.orderReminderEmailCustomSubject
        );

        this.orderReminderEmailCustomContentField.setValues(
            model == null ? undefined : model.orderReminderEmailCustomContent
        );

        this.sendCancellationEmailSwitch.setValue(sendCancellationEmail);
        this.cancellationEmailCustomSubjectField.setValues(
            model == null ? undefined : model.cancellationEmailCustomSubject
        );

        this.cancellationEmailCustomContentField.setValues(
            model == null ? undefined : model.cancellationEmailCustomContent
        );

        this.sendConfirmationEmailOnReservationAcceptanceSwitch.setValue(sendConfirmationEmailOnReservationAcceptance);
        this.reservationConfirmationEmailCustomSubjectField.setValues(
            model == null ? undefined : model.reservationConfirmationEmailCustomSubject
        );

        this.reservationConfirmationEmailCustomContentField.setValues(
            model == null ? undefined : model.reservationConfirmationEmailCustomContent
        );

        this.tableReservationsEnabledSwitch.setValue(model?.tableReservationsEnabled ?? false);
        this.tableReservationsEnabledSwitch.onChange = (value) => {
            this.setTableReservationSectionVisibility(value);
        };

        const locationId = model?.locationId ?? CurrentLocationService.getLocationId();
        if (locationId) {
            Factory.getInstance<LocationDao>(LocationDao)
                .getSingle(locationId)
                .then((location) => {
                    this.visibleEndUserPropertiesSelect.setAllowedOptions(
                        location.privacySettings?.visibleEndUserProperties
                    );
                });
        }

        this.deliveryAreasTable!.setRows(model?.deliveryAreas ?? []);

        this.setOrdererRequirementsSectionVisibility(
            (model?.ordererRequirements.enabledFieldRequirementsCount ?? 0) > 0
        );

        this.setUnpaidOrderEmailSectionVisibility(sendEmailConfirmationsForUnpaidOrders);
        this.setOrderReminderEmailSectionVisibility(sendOrderReminderByEmail);
        this.setCancellationEmailSectionVisibility(sendCancellationEmail);
        this.setFulfillmentHandlingSectionVisibility(fulfillmentPositionDisplayMode);
        this.setExcludedFulfillmentGroupsSectionVisibility(fulfillmentPositionDisplayMode);
        this.setEnableNotificationFormVisibility(fulfillmentHandling);
        this.setDefaultTipSectionVisibility(useTip);
        this.setNotificationSectionVisibility(enableNotifications);
        this.setBeforeOrderPlacedFormVisibility();
        this.setEnableOrderReminderRowVisibility(usePreorder === "true");
        this.setTableReservationsEnabledRowVisibility(usePreorder === "true");
        this.setMaxPreorderPeriodVisibility(usePreorder === "true");
        this.setOrderPlacedTextIfNotificationsEnabledFormVisibility();
        this.setOrderReminderPushNotificationSectionVisibility(sendOrderReminderByPushNotification);
        this.setTableReservationSectionVisibility(model?.tableReservationsEnabled ?? false);
        this.setReservationConfirmationEmailSectionVisibility(sendConfirmationEmailOnReservationAcceptance);
        this.setDeliveryAreasSectionVisibility();

        if (model !== undefined && model.paymentMethods !== undefined && model.paymentMethods.length > 0) {
            const paymentMethodsDiv = $(".order-application-payment-methods-group");
            paymentMethodsDiv.children().remove();

            // Add section online payment methods
            paymentMethodsDiv.append(
                `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.online")}</div>`
            );

            // Add headers
            paymentMethodsDiv.append(
                `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
            );

            // Add rows
            const browserPaymentMethods = model.paymentMethods.filter(
                (i) => !i.isPaymentDevice && i.mode === PaymentMethodMode.Online
            );
            for (const paymentMethod of browserPaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }

            const onlinePaymentDevicePaymentMethods = model.paymentMethods.filter(
                (pm) => pm.mode === PaymentMethodMode.Online && pm.isPaymentDevice
            );
            if (onlinePaymentDevicePaymentMethods.length > 0) {
                // Add section offline payment methods
                paymentMethodsDiv.append(
                    `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.paymentDevices")}</div>`
                );

                // Add headers
                paymentMethodsDiv.append(
                    `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
                );

                // Add rows
                for (const paymentMethod of onlinePaymentDevicePaymentMethods) {
                    this.insertPaymentMethodRow(paymentMethod);
                }
            }

            // Add section revenue effective offline payment methods
            paymentMethodsDiv.append(
                `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.offline")}</div>`
            );

            // add headers
            paymentMethodsDiv.append(
                `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
            );

            // add rows
            const offlineRevenueEffectivePaymentMethods = model.paymentMethods.filter(
                (i) => i.mode === PaymentMethodMode.Offline && i.revenueEffective
            );
            for (const paymentMethod of offlineRevenueEffectivePaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }

            // Add section not revenue-effective offline payment methods
            const offlineNotRevenueEffectivePaymentMethods = model.paymentMethods.filter(
                (i) => i.mode === PaymentMethodMode.Offline && !i.revenueEffective
            );
            if (offlineNotRevenueEffectivePaymentMethods.length > 0) {
                paymentMethodsDiv.append(
                    `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.notRevenueEffective")}</div>`
                );

                // add headers
                paymentMethodsDiv.append(
                    `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
                );
            }

            // add rows
            for (const paymentMethod of offlineNotRevenueEffectivePaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }

            const payLaterPaymentMethod = model.paymentMethods.find((pm) => pm.type === config.payLaterPaymentMethod);
            this.setPaymentMethodsOptionsSectionVisibility(
                payLaterPaymentMethod != null &&
                    (payLaterPaymentMethod.isEnabledForGuests || payLaterPaymentMethod.isEnabledForWaiters)
            );
            this.setOrderConfirmationSectionVisibility(
                payLaterPaymentMethod != null &&
                    (payLaterPaymentMethod.isEnabledForGuests || payLaterPaymentMethod.isEnabledForWaiters)
            );
        } else {
            this.setPaymentMethodsOptionsSectionVisibility(false);
            this.setOrderConfirmationSectionVisibility(false);
        }
    }

    private insertPaymentMethodRow(paymentMethod: IUseCasePaymentMethod) {
        const index = this.originalModel!.paymentMethods!.indexOf(paymentMethod);
        const paymentMethodDiv = $('<div class="form-group row"></div').appendTo($(".order-application-payment-methods-group"));
        paymentMethodDiv.append(
            `<label class="col-2 col-form-label">${translate(paymentMethod.localizedName)}</label>`
        );

        paymentMethodDiv.append(`<div class="col-2"><input type="checkbox" name="isEnabledForGuests${index}" /></div>`);
        const isEnabledForGuestsSwitch = new Switch(this.$form.find(`input[name="isEnabledForGuests${index}"]`));
        isEnabledForGuestsSwitch.setValue(paymentMethod.isEnabledForGuests);

        paymentMethodDiv.append(
            `<div class="col-2"><input type="checkbox" name="isEnabledForWaiters${index}" /></div>`
        );
        const isEnabledForWaitersSwitch = new Switch(this.$form.find(`input[name="isEnabledForWaiters${index}"]`));
        isEnabledForWaitersSwitch.setValue(paymentMethod.isEnabledForWaiters);

        const isQuickPaymentEnabledColumn = $(`<div class="col-2"></div>`).appendTo(paymentMethodDiv);
        const isQuickPaymentEnabledSection = $(`<div></div>`).appendTo(isQuickPaymentEnabledColumn);

        let isQuickPaymentEnabledSwitch: Switch;
        if (paymentMethod.isQuickPaymentAvailable) {
            $(`<input type="checkbox" name="isQuickPaymentEnabled${index}" />`).appendTo(isQuickPaymentEnabledSection);
            isQuickPaymentEnabledSwitch = new Switch(this.$form.find(`input[name="isQuickPaymentEnabled${index}"]`));
            isQuickPaymentEnabledSwitch.setValue(paymentMethod.isQuickPaymentEnabled);
        }

        const permissionSection = $('<div class="col-2"></div>').appendTo(paymentMethodDiv);
        const userSelect = new SelectBootstrap<ILinkableEntity>(
            $('<select name="user" class="form-control"></select>').appendTo(permissionSection),
            this.waiterDataSource,
            (c) => c.name,
            false,
            i18n("general.all"),
            undefined,
            true
        );

        userSelect.setValues(paymentMethod.permittedUserIds);

        this.configurePermissionSection(paymentMethod.isEnabledForWaiters, permissionSection);
        this.configureQuickPaymentSection(paymentMethod.isEnabledForWaiters, isQuickPaymentEnabledSection);

        isEnabledForWaitersSwitch.onChange = () => {
            if (paymentMethod.paymentMethodId === config.payLaterPaymentMethod) {
                const showPrintoutsSection =
                    isEnabledForGuestsSwitch.getValue() || isEnabledForWaitersSwitch.getValue();
                this.setPaymentMethodsOptionsSectionVisibility(showPrintoutsSection);
            }

            this.configurePermissionSection(isEnabledForWaitersSwitch.getValue(), permissionSection);

            if (paymentMethod.isQuickPaymentAvailable) {
                this.configureQuickPaymentSection(isEnabledForWaitersSwitch.getValue(), isQuickPaymentEnabledSection);
            }

            const isEnabledForGuests = isEnabledForGuestsSwitch.getValue();
            const isEnabledForWaiters = isEnabledForWaitersSwitch.getValue();
            this.setOrderConfirmationSectionVisibility(isEnabledForGuests || isEnabledForWaiters);
        };

        isEnabledForGuestsSwitch.onChange = () => {
            if (paymentMethod.paymentMethodId === config.payLaterPaymentMethod) {
                const isEnabledForGuests = isEnabledForGuestsSwitch.getValue();
                const isEnabledForWaiters = isEnabledForWaitersSwitch.getValue();
                const showPrintoutsSection = isEnabledForGuests || isEnabledForWaiters;
                this.setPaymentMethodsOptionsSectionVisibility(showPrintoutsSection);
                this.setOrderConfirmationSectionVisibility(isEnabledForGuests || isEnabledForWaiters);

                if (!isEnabledForGuests) {
                    this.sendEmailConfirmationsForUnpaidOrdersSwitch.setValue(false);
                }
            }
        };

        this.paymentMethodToggles[index] = {
            payNow: isEnabledForGuestsSwitch,
            payLater: isEnabledForWaitersSwitch,
            quickPayment: isQuickPaymentEnabledSwitch!,
            userSelect: userSelect,
        };
    }

    private configureQuickPaymentSection(
        isPaymentMethodEnabledForWaiters: boolean,
        isQuickPaymentEnabledSection: JQuery
    ) {
        if (isPaymentMethodEnabledForWaiters) {
            isQuickPaymentEnabledSection.removeClass("hidden");
        } else {
            isQuickPaymentEnabledSection.addClass("hidden");
        }
    }

    private configurePermissionSection(isPaymentMethodEnabledForWaiters: boolean, permissionSection: JQuery) {
        if (isPaymentMethodEnabledForWaiters) {
            permissionSection.removeClass("hidden");
        } else {
            permissionSection.addClass("hidden");
        }
    }

    private setPaymentMethodsOptionsSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.paymentMethodsOptionsSection.removeClass("hidden");
        } else {
            this.paymentMethodsOptionsSection.addClass("hidden");
        }
    }

    private setUnpaidOrderEmailSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.unpaidOrderEmailSection.removeClass("hidden");
        } else {
            this.unpaidOrderEmailSection.addClass("hidden");
        }
    }

    private setOrderReminderEmailSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.orderReminderEmailSection.removeClass("hidden");
        } else {
            this.orderReminderEmailSection.addClass("hidden");
        }
    }

    private setCancellationEmailSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.cancellationEmailSection.removeClass("hidden");
        } else {
            this.cancellationEmailSection.addClass("hidden");
        }
    }

    private setReservationConfirmationEmailSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.reservationConfirmationEmailSection.removeClass("hidden");
        } else {
            this.reservationConfirmationEmailSection.addClass("hidden");
        }
    }

    private setOrderConfirmationSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.orderConfirmationSections.removeClass("hidden");
        } else {
            this.orderConfirmationSections.addClass("hidden");
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateOrderApplicationCmd {
        // eslint-disable-next-line no-extra-boolean-cast
        const paymentMethods = !!this.originalModel
            ? this.originalModel.paymentMethods!.map((element, index) => {
                  element.isEnabledForGuests = this.paymentMethodToggles[index].payNow.getValue();
                  element.isEnabledForWaiters = this.paymentMethodToggles[index].payLater.getValue();
                  element.isQuickPaymentEnabled =
                      element.isEnabledForWaiters &&
                      this.paymentMethodToggles[index].quickPayment != null &&
                      this.paymentMethodToggles[index].quickPayment!.getValue();
                  element.permittedUserIds = this.paymentMethodToggles[index].userSelect.getValues();
                  return element;
              })
            : undefined;

        const allowCreatingOrderBills = this.allowCreatingOrderBillsSwitch.getValue();
        const ordererRequirementsEnabled = this.ordererRequirementsEnabledSwitch.getValue()!;


        let hasError = false;




        const data: ICreateOrderApplicationCmd = {
            name: this.nameField.getValues(),
            areItemCommentsAllowed: this.areItemCommentsAllowedSwitch.getValue(),
            minimumTotalUnitsPerOrder: this.minimumTotalUnitsPerOrderField.getValue(),
            maximumTotalUnitsPerOrder: this.maximumTotalUnitsPerOrderField.getValue(),
            useTip: this.useTipSwitch.getValue(),
            useDailyOrderIncrement: this.useDailyOrderIncrementSwitch.getValue(),
            orderPlacedText: this.orderPlacedTextField.getValues(),
            beforeOrderPlacedText: this.beforeOrderPlacedTextField.getValues(),
            fulfillmentPositionDisplayMode: parseInt(this.fulfillmentPositionDisplayModeSelect.getValue()!),
            fulfillmentHandling: parseInt(this.fulfillmentHandlingSelect.getValue()!),
            schedulingMode: parseInt(this.fulfillmentTimeModeSelect.getValue()!),
            consumptionSite: parseInt(this.consumptionSiteSelect.getValue()!),
            consumptionSiteDisplayMode: parseInt(this.consumptionSiteDisplayModeSelect.getValue()!),
            usePreorder: this.usePreorderSelect.getValue() === "true",
            paymentMethods: paymentMethods!,
            pushNotificationPickUpOrderMessage: this.notificationPickUpOrderMessageTextField.getValues(),
            pushNotificationPickUpOrderTitle: this.notificationPickUpOrderTitleTextField.getValues(),
            enableNotifications: this.enableNotificationsSwitch.getValue(),
            enablePushNotificationsOnIos: this.enablePushNotificationsOnIosSwitch.getValue(),
            enablePushNotificationsOnAndroid: this.enablePushNotificationsOnAndroidSwitch.getValue(),
            defaultTipPercent: this.defaultTipPercentField.getValue()!,
            enableSignUpBeforePay: !this.hideRegisterButtonSwitch.getValue(),
            markUnpaidOrdersOnPrintouts: this.markUnpaidOrdersOnPrintoutsSwitch.getValue(),
            maxPreorderPeriod: this.maxPreorderPeriodField.getValue(),
            orderPlacedTextIfNotificationsEnabled: this.orderPlacedTextIfNotificationsEnabledField.getValues(),
            visibleEndUserProperties: this.visibleEndUserPropertiesSelect.getValue(),
            oneFulfillmentPerOrderMode: parseInt(this.oneFulfillmentPerOrderModeSelect.getValue()!),
            allowCreatingOrderBills: allowCreatingOrderBills,
            sendEmailConfirmationsForUnpaidOrders: this.sendEmailConfirmationsForUnpaidOrdersSwitch.getValue(),
            sendOrderReminderByPushNotification: this.sendOrderReminderByPushNotificationSwitch.getValue(),
            sendOrderReminderByEmail: this.sendOrderReminderByEmailSwitch.getValue(),
            orderReminderPushNotificationTitle: this.orderReminderPushNotificationTitleTextField.getValues(),
            orderReminderPushNotificationMessage: this.orderReminderPushNotificationMessageTextField.getValues(),
            useVouchersAndDiscounts: this.useVouchersAndDiscountsSwitch.getValue()!,
            promptOperatorsForReceiptAfterPayment: this.promptOperatorsForReceiptAfterPaymentSwitch.getValue()!,
            unpaidOrderEmailCustomSubject: this.unpaidOrderEmailCustomSubjectField.getValues()!,
            unpaidOrderEmailCustomContent: this.unpaidOrderEmailCustomContentField.getValues()!,
            orderReminderEmailCustomSubject: this.orderReminderEmailCustomSubjectField.getValues()!,
            orderReminderEmailCustomContent: this.orderReminderEmailCustomContentField.getValues()!,
            orderPaymentReceiptEmailCustomSubject: this.orderPaymentReceiptEmailCustomSubjectField.getValues()!,
            orderPaymentReceiptEmailCustomContent: this.orderPaymentReceiptEmailCustomContentField.getValues()!,
            sendOrderReminderBeforeFulfillmentTime: this.sendOrderReminderByEmailSwitch.getValue()
                ? this.sendOrderReminderBeforeFulfillmentTimePicker.getValidatedDuration()
                : undefined,
            ordererRequirements: ordererRequirementsEnabled
                ? {
                      firstNameRequirement: parseInt(this.firstNameRequirementSelect.getValue()!),
                      lastNameRequirement: parseInt(this.lastNameRequirementSelect.getValue()!),
                      emailRequirement: parseInt(this.emailRequirementSelect.getValue()!),
                      phoneNumberRequirement: parseInt(this.phoneNumberRequirementSelect.getValue()!),
                      registeredRequirement: parseInt(this.registeredRequirementSelect.getValue()!),
                  }
                : undefined,
            orderBillCustomNameSource: allowCreatingOrderBills
                ? parseInt(this.orderBillCustomNameSourceSelect.getValue()!)
                : undefined,
            excludedFulfillmentGroupIds: this.excludedFulfillmentGroupsSelect.getValues(),
            sendCancellationEmail: this.sendCancellationEmailSwitch.getValue(),
            cancellationEmailCustomContent: this.cancellationEmailCustomContentField.getValues()!,
            cancellationEmailCustomSubject: this.cancellationEmailCustomSubjectField.getValues()!,
            sendConfirmationEmailOnReservationAcceptance:
                this.sendConfirmationEmailOnReservationAcceptanceSwitch.getValue(),
            reservationConfirmationEmailCustomContent: this.reservationConfirmationEmailCustomContentField.getValues()!,
            reservationConfirmationEmailCustomSubject: this.reservationConfirmationEmailCustomSubjectField.getValues()!,
            tableReservationsEnabled: this.tableReservationsEnabledSwitch.getValue()!,
            deliveryAreas: [],
            operatorDisplayMainItemPricesOnOfferOverview:
                this.operatorDisplayMainItemPricesOnOfferOverviewSwitch.getValue(),
            operatorDisplayItemImages: this.operatorDisplayItemImagesSwitch.getValue(),
            kioskPaymentPrompt: this.kioskPaymentPromptField.getValues(),
        };


        this.$deliveryAreasSectionTitle.removeClass("is-invalid");
        if (!this.deliveryAreasTable!.validateRows()) {
            hasError = true;
        } else {
            data.deliveryAreas = this.deliveryAreasTable!.getValidRows();
        }

        if (data.schedulingMode === SchedulingMode.DeliveryTime && data.consumptionSiteDisplayMode === ConsumptionSiteDisplayMode.DeliveryAddress) {
            if (data.deliveryAreas.length === 0) {
                this.$deliveryAreasSectionTitle.addClass("is-invalid");
                DomUtils.scrollTo(this.$deliveryAreasSection);
                hasError = true;
            }
        }

        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 64) ||
                (data.name.fr !== undefined && data.name.fr.length > 64) ||
                (data.name.it !== undefined && data.name.it.length > 64) ||
                (data.name.en !== undefined && data.name.en.length > 64)
            ) {
                hasError = true;
                this.nameField.setError(true);
                this.nameField.setError(false);
            }
        }

        if (
            data.schedulingMode !== SchedulingMode.None ||
            data.oneFulfillmentPerOrderMode !== OneFulfillmentPerOrderMode.NotForced
        ) {
            if (data.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.None) {
                hasError = true;
                this.fulfillmentPositionDisplayModeSelect.setError(true);
            } else {
                this.fulfillmentPositionDisplayModeSelect.setError(false);
            }
        }

        if (data.usePreorder) {
            if (data.schedulingMode === SchedulingMode.None) {
                hasError = true;
                this.fulfillmentTimeModeSelect.setError(true);
            } else {
                this.fulfillmentTimeModeSelect.setError(false);
            }
        }

        if (
            data.orderPlacedText.de === undefined &&
            data.orderPlacedText.en === undefined &&
            data.orderPlacedText.fr === undefined &&
            data.orderPlacedText.it === undefined
        ) {
            hasError = true;
            this.orderPlacedTextField.setError(true);
        } else {
            if (
                (data.orderPlacedText.de !== undefined && data.orderPlacedText.de.length > 1024) ||
                (data.orderPlacedText.fr !== undefined && data.orderPlacedText.fr.length > 1024) ||
                (data.orderPlacedText.it !== undefined && data.orderPlacedText.it.length > 1024) ||
                (data.orderPlacedText.en !== undefined && data.orderPlacedText.en.length > 1024)
            ) {
                hasError = true;
                this.orderPlacedTextField.setError(true);
            } else {
                this.orderPlacedTextField.setError(false);
            }
        }

        if (data.schedulingMode !== SchedulingMode.None) {
            if (
                (data.beforeOrderPlacedText.de !== undefined && data.beforeOrderPlacedText.de.length > 1024) ||
                (data.beforeOrderPlacedText.fr !== undefined && data.beforeOrderPlacedText.fr.length > 1024) ||
                (data.beforeOrderPlacedText.it !== undefined && data.beforeOrderPlacedText.it.length > 1024) ||
                (data.beforeOrderPlacedText.en !== undefined && data.beforeOrderPlacedText.en.length > 1024)
            ) {
                hasError = true;
                this.beforeOrderPlacedTextField.setError(true);
            } else {
                this.beforeOrderPlacedTextField.setError(false);
            }
        }

        if (
            data.minimumTotalUnitsPerOrder !== undefined &&
            data.maximumTotalUnitsPerOrder !== undefined &&
            data.minimumTotalUnitsPerOrder > data.maximumTotalUnitsPerOrder
        ) {
            hasError = true;
            this.minimumTotalUnitsPerOrderField.setError(true);
            this.maximumTotalUnitsPerOrderField.setError(true);
        } else {
            this.minimumTotalUnitsPerOrderField.setError(false);
            this.maximumTotalUnitsPerOrderField.setError(false);
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (hasError ? undefined : data) as any;
    }

    private getDefaultOrderPlacedTextIfNotificationsEnabled(
        selectedFulfillmentPositionDisplayMode: FulfillmentPositionDisplayMode
    ): IMultiLanguageText | undefined {
        let orderPlacedTextIfNotificationsEnabled: IMultiLanguageText | undefined = undefined;
        if (selectedFulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position) {
            orderPlacedTextIfNotificationsEnabled = {
                en: i18n("notification.notifiedWhenOrderReadyWithPosition", { lng: "en" }),
                de: i18n("notification.notifiedWhenOrderReadyWithPosition", { lng: "de" }),
                fr: i18n("notification.notifiedWhenOrderReadyWithPosition", { lng: "fr" }),
                it: i18n("notification.notifiedWhenOrderReadyWithPosition", { lng: "it" }),
            };
        }

        if (selectedFulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Duration) {
            orderPlacedTextIfNotificationsEnabled = {
                en: i18n("notification.notifiedWhenOrderReadyWithDuration", { lng: "en" }),
                de: i18n("notification.notifiedWhenOrderReadyWithDuration", { lng: "de" }),
                fr: i18n("notification.notifiedWhenOrderReadyWithDuration", { lng: "fr" }),
                it: i18n("notification.notifiedWhenOrderReadyWithDuration", { lng: "it" }),
            };
        }
        return orderPlacedTextIfNotificationsEnabled;
    }

    private getDefaultMessageToUserBeforeOrder(): IMultiLanguageText {
        return {
            en: i18n("useCase.defaultMessageToUserBeforeOrder", { lng: "en" }),
            de: i18n("useCase.defaultMessageToUserBeforeOrder", { lng: "de" }),
            fr: i18n("useCase.defaultMessageToUserBeforeOrder", { lng: "fr" }),
            it: i18n("useCase.defaultMessageToUserBeforeOrder", { lng: "it" }),
        };
    }

    private getDefaultOrderReminderPushNotificationMessage(): IMultiLanguageText {
        return {
            en: i18n("useCase.defaultOrderReminderPushNotificationMessage", { lng: "en" }),
            de: i18n("useCase.defaultOrderReminderPushNotificationMessage", { lng: "de" }),
            fr: i18n("useCase.defaultOrderReminderPushNotificationMessage", { lng: "fr" }),
            it: i18n("useCase.defaultOrderReminderPushNotificationMessage", { lng: "it" }),
        };
    }

    private getDefaultOrderReminderPushNotificationTitle(): IMultiLanguageText {
        return {
            en: i18n("useCase.defaultOrderReminderPushNotificationTitle", { lng: "en" }),
            de: i18n("useCase.defaultOrderReminderPushNotificationTitle", { lng: "de" }),
            fr: i18n("useCase.defaultOrderReminderPushNotificationTitle", { lng: "fr" }),
            it: i18n("useCase.defaultOrderReminderPushNotificationTitle", { lng: "it" }),
        };
    }

    private getDefaultPickUpOrderPushNotificationTitle(): IMultiLanguageText {
        const key = "useCase.pickupOrderPushNotificationTitle";
        return {
            en: i18n(key, { lng: "en" }),
            de: i18n(key, { lng: "de" }),
            fr: i18n(key, { lng: "fr" }),
            it: i18n(key, { lng: "it" }),
        };
    }

    private getDefaultPickUpOrderPushNotificationMessage(): IMultiLanguageText {
        const key = "useCase.pickupOrderPushNotificationMessage";

        return {
            en: i18n(key, { lng: "en" }),
            de: i18n(key, { lng: "de" }),
            fr: i18n(key, { lng: "fr" }),
            it: i18n(key, { lng: "it" }),
        };
    }

    protected override handleApiError(type: string, error?: DaoError) {
        const modifiedEntityId = error?.additionalData?.modifiedEntityId;
        if (modifiedEntityId != null && error?.type === exceptionTypes.preorderRoomsWithoutPlanningException) {
            const roomNames = error?.additionalData.roomNames ?? "";

            if (!this.saveAndCopy) {
                switch (type) {
                    case "create":
                        this.showSuccess(i18n("general.successfullyCreated"));
                        break;
                    case "update":
                        this.showSuccess(i18n("general.successfullyUpdated"));
                        break;
                }
            }

            UiNotification.showWarning(
                i18n("useCase.preorderRoomsWithoutPlanningException", { roomNames: roomNames }),
                undefined,
                () => {
                    if (this.saveAndCopy) {
                        location.href = this.getBasePath() + "new" + "?objectIdToCopy=" + modifiedEntityId;
                        return;
                    }

                    if (this.useRedirect) {
                        if (this.redirectPathProvider !== undefined) location.href = this.redirectPathProvider();
                        else location.href = this.getBasePath() + modifiedEntityId;
                    }
                }
            );
        } else {
            let message =
                this["resolveErrorMessage"] !== undefined ? this["resolveErrorMessage"](type, error!) : undefined;
            if (message === undefined) {
                switch (type) {
                    case "read":
                        message = i18n("general.failedLoad");
                        break;
                    case "create":
                        message = i18n("general.failedCreate");
                        break;
                    case "update":
                        message = i18n("general.failedUpdate");
                        break;
                    case "delete":
                        message = i18n("general.failedRemove");
                        break;
                }
            }

            UiNotification.showException(message!);
        }
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.atLeastOneDeliveryAreaRequiredException:
                DomUtils.scrollTo(this.$deliveryAreasSection);
                return i18n("useCase.atLeastOneDeliveryAreaRequiredError");
            case exceptionTypes.minimumTotalUnitsPerOrderGreaterThanMaximumException:
                this.minimumTotalUnitsPerOrderField.setError(true);
                this.maximumTotalUnitsPerOrderField.setError(true);
                this.scrollToFirstErrorField();
                return i18n("useCase.minimumTotalUnitsPerOrderGreaterThanMaximumError");
            case exceptionTypes.minimumTotalUnitsPerOrderInvalidException:
                this.minimumTotalUnitsPerOrderField.setError(true);
                this.scrollToFirstErrorField();
                return i18n("useCase.minimumTotalUnitsPerOrderInvalidError");
            case exceptionTypes.maximumTotalUnitsPerOrderInvalidException:
                this.maximumTotalUnitsPerOrderField.setError(true);
                this.scrollToFirstErrorField();
                return i18n("useCase.maximumTotalUnitsPerOrderInvalidError");
            case "delivery_area_overlapping_postal_codes_exception":
                DomUtils.scrollTo(this.$deliveryAreasSection);
                return "Overlapping postal codes"
            case "entity_in_use_exception":
                let name = b.additionalData.dependingName;
                if (name == null || name === undefined)
                    name = i18n("enums.ServiceType." + ServiceType[b.additionalData.serviceType]);

                return i18n("useCase.failedDelete", { dependingName: name });
            case "too_many_quick_payment_methods_enabled_exception":
                const maxEnabledPaymentMethodsCount = b.additionalData.maxEnabledPaymentMethodsCount;
                return i18n("useCase.tooManyQuickPaymentMethodsEnabled", {
                    maxEnabledPaymentMethodsCount: maxEnabledPaymentMethodsCount,
                });
        }

        return undefined;
    }
}
